import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
    FormType,
    Description,
    UIElementType,
    UILayout,
    EditorUIContainer,
    Entity,
    FormSplitConfiguration
} from '#types/System.types';
import {
    FormDeriveEntry,
    createFormDeriveEngineConfig,
    resolveFormDeriveLockedFieldIds
} from '#types';
import type { ArTableType } from '#types';
import { FormStateId } from '#types/System.Parameters.types';
import { useLayoutEditorBase } from './useLayoutEditorBase';
import { useAdd } from '../data/useAdd';
import { useSet } from '../data/useSet';
import { useGet } from '../data/useGet';
import { deleteField } from 'firebase/firestore';
import { useArchive } from '../archive/useArchive';
import { createCollectionStore } from '../../store';
import { validateFormDeriveDraft } from '@/components/settings/formDerives/validateFormDeriveDraft';
import { FormDerivesBlockedError } from '@/components/settings/formDerives/FormDerivesBlockedError';
import { FormDerivesUncheckedError } from '@/components/settings/formDerives/FormDerivesUncheckedError';

const formTypesStore = createCollectionStore<FormType>('formTypes');
const entitiesStore = createCollectionStore<Entity>('entities');

type UseLayoutEditorFormProps = {
    /**
     * Every ArTableType the editor holds, supplied by `LayoutEditorProvider` so
     * the save-time derive gate judges cycles against the same
     * `formulaSequence`s the pane shows and no second listener is opened. A
     * referenced table missing from this list blocks the save rather than
     * reporting "no cycle".
     */
    tableTypes: readonly ArTableType[];
    /**
     * True while `tableTypes` is still arriving, so the gate can tell a table
     * that does not exist from one that has not loaded. The provider hands `[]`
     * in both cases, and only this flag separates them.
     */
    tableTypesLoading: boolean;
};

/**
 * Custom hook for managing form layout editing functionality.
 * Provides comprehensive state management and operations for form types including:
 * - Form type selection, creation, and deletion
 * - State management (Draft, Review, etc.)
 * - Layout modifications
 * - Description updates
 * - Allowed states and transitions
 * - Save operations
 * - Derived-value configuration (`formDerives`)
 *
 * `onSave` writes `formDerives` and the denormalized `protected.derivedFieldIds`
 * in the one `set('formTypes', …)` call, never two: `firestore.rules`'
 * `noProtectedFieldsChanged` reads the denormalized list, so a split write leaves
 * a window where a client save is wrongly refused or wrongly permitted.
 * `formDeriveRev` is server-owned and never written here.
 *
 * Before it writes anything, `onSave` runs `validateFormDeriveDraft` over the
 * draft plus every table type's `formulaSequence` and throws
 * `FormDerivesBlockedError` when the verdict is blocked — the WHOLE FormType save
 * aborts, because an entry the engine would drop computes nothing, does not bump
 * `formDeriveRev`, and so is never repaired by anything downstream. The throw
 * also sets `formDerivesSaveRefused`, which the Derived values pane reads to open
 * the cycle modal; a save that proceeds clears it.
 *
 * ONE rule covers the loading window, chosen over waiting for the tables because
 * a save that blocks on a subscription is far harder to reason about than a save
 * that asks to be pressed again: while `tableTypesLoading` and the draft names a
 * table that has not arrived, there is no verdict at all, so the save refuses
 * with `FormDerivesUncheckedError` and nothing is written or flagged. Any other
 * problem in the draft is reported on the retry, when the whole verdict is real.
 * Judging on the tables the editor happens to hold is what made a correct
 * configuration read as "missing table" and jump the toolbar to the pane.
 *
 * @returns {Object} Form editor state and methods
 * @property {FormType | null} structure - Currently selected form type structure
 * @property {FormType[]} structures - List of available form types
 * @property {string | undefined} selectedStructureId - ID of the currently selected structure
 * @property {Entity[]} entities - List of available entities
 */
export const useLayoutEditorForm = ({ tableTypes, tableTypesLoading }: UseLayoutEditorFormProps) => {
    const { add } = useAdd();
    const { archiveFormType } = useArchive();
    const { set } = useSet();
    const { get } = useGet();
    const [selectedStructureId, setSelectedStructureId] = useState<string | undefined>();
    const [structure, setStructure] = useState<FormType | null>(null);
    const [formDerivesSaveRefused, setFormDerivesSaveRefused] = useState(false);

    useEffect(() => {
        formTypesStore.connect();
        entitiesStore.connect();
        return () => {
            formTypesStore.disconnect();
            entitiesStore.disconnect();
        };
    }, []);

    const formTypesSnap = useSnapshot(formTypesStore.state);
    const structures = useMemo<FormType[]>(() => {
        const result: FormType[] = [];
        for (const id of formTypesSnap.docIds) {
            const entry = formTypesSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as FormType), docId: id });
        }
        return result;
    }, [formTypesSnap.docIds, formTypesSnap.docs]);

    const entitiesSnap = useSnapshot(entitiesStore.state);
    const entities = useMemo<Entity[]>(() => {
        const result: Entity[] = [];
        for (const id of entitiesSnap.docIds) {
            const entry = entitiesSnap.docs.get(id);
            if (entry) result.push({ ...(entry.data as Entity), docId: id });
        }
        return result;
    }, [entitiesSnap.docIds, entitiesSnap.docs]);

    const baseEditor = useLayoutEditorBase({
        onSave: async (layout: UILayout, description: Description, state?: any) => {
            if (!structure || !selectedStructureId) throw new Error('No structure selected');

            const formDerivesDraft: FormDeriveEntry[] = state?.formDerives ?? [];
            const derivesVerdict = validateFormDeriveDraft({
                entries: formDerivesDraft,
                tableTypes
            });
            if (tableTypesLoading && derivesVerdict.missingTableIds.length > 0) {
                throw new FormDerivesUncheckedError(derivesVerdict.missingTableIds);
            }
            if (derivesVerdict.blocked) {
                setFormDerivesSaveRefused(true);
                throw new FormDerivesBlockedError(derivesVerdict);
            }
            setFormDerivesSaveRefused(false);

            const derivedFieldIds = [...resolveFormDeriveLockedFieldIds(
                createFormDeriveEngineConfig({ formDerives: formDerivesDraft }).entries
            )];
            const protectedUpdate = state?.protected === undefined && derivedFieldIds.length === 0
                ? deleteField()
                : { ...(state?.protected ?? {}), derivedFieldIds };

            const updates: Partial<FormType> = {
                defaultUILayout: (
                    ({ meta, ...rest }) => rest)(layout as UILayout & { meta?: any }
                ),
                description,
                possibleStates: state?.possibleStates,
                allowedNextForms: state?.allowedNextForms,
                splitConfiguration: state?.splitConfiguration,
                protected: protectedUpdate as any,
                formDerives: (formDerivesDraft.length > 0 ? formDerivesDraft : deleteField()) as any,
            };

            await set('formTypes', selectedStructureId, updates);
        }
    });

    const { 
        dispatch, 
        layoutEditorState 
    } = baseEditor;

    /**
     * Clears the currently selected structure (Form Type)
     */
    const clearSelectedStructure = () => {
        setStructure(null);
    };

    /**
     * Updates the selected state and its allowed next states configuration
     * @param {FormStateId} stateId - The state to update
     * @param {FormStateId[]} allowedStates - Array of states that can follow this state
     */
    const handleStateChange = (stateId: FormStateId, allowedStates: FormStateId[]) => {
        dispatch({
            type: 'FORM_STATES_CONFIG',
            payload: {
                type: 'updateNextStates',
                stateId,
                allowedNextStates: allowedStates
            }
        });
    };

    /**
     * Updates a specific field in the structure description
     * @param {keyof Description} field - The description field to update
     * @param {string} value - New value for the field
     */
    const handleDescriptionChange = (field: keyof Description, value: string) => {
        if (!structure || !selectedStructureId) return;
        
        setStructure(prev => prev ? {
            ...prev,
            description: {
                ...prev.description,
                [field]: value
            }
        } : null);

        dispatch({
            type: 'UPDATE_DESCRIPTION',
            payload: {
                ...structure.description,
                [field]: value
            }
        });
    };

    /**
     * Updates the list of forms that can follow this form type
     * @param {FormType["docId"][]} forms - Array of form type IDs that can follow
     */
    const handleAllowedFormsChange = (forms: FormType["docId"][]) => {
        dispatch({
            type: 'FORM_STATES_CONFIG',
            payload: {
                type: 'updateNextForms',
                forms
            }
        });
    };

    /**
     * Updates the list of possible states for this form type
     * @param {FormStateId[]} states - Array of states to set as possible states
     */
    const handleStatesUpdate = (states: FormStateId[]) => {
        dispatch({
            type: 'FORM_STATES_CONFIG',
            payload: {
                type: 'update',
                states
            }
        });
    };

    /**
     * Retrieves the entry and exit rules for a specific state
     * @param {string} stateName - Name of the state to get rules for
     * @returns {Array<{id: string, type: 'Entry' | 'Exit', [key: string]: any}>} Array of rules with their type
     */
    const getStateRules = (stateName: string) => {
        if (!layoutEditorState.possibleStates) return [];
        const currentState = layoutEditorState.possibleStates[stateName];
        return [
            ...(currentState?.entryRules ? Object.entries(currentState.entryRules).map(([id, rule]) => ({
                id,
                type: 'Entry',
                ...rule
            })) : []),
            ...(currentState?.exitRules ? Object.entries(currentState.exitRules).map(([id, rule]) => ({
                id,
                type: 'Exit',
                ...rule
            })) : [])
        ];
    };

    /**
     * Checks if a state can be safely removed
     * @param {FormStateId} stateId - ID of the state to check
     * @returns {{ canDelete: boolean, reason?: string }} Object indicating if state can be removed and reason if not
     */
    const canRemoveState = (stateId: FormStateId) => {
        if (!layoutEditorState.possibleStates) {
            return { canDelete: false, reason: 'Form type not loaded' };
        }

        if (stateId === 'draft') {
            return { canDelete: false, reason: 'Cannot remove the draft state' };
        }

        const states = layoutEditorState.possibleStates;
        const isStateSelected = stateId in states;

        if (!isStateSelected) {
            return { canDelete: true };
        }

        if (Object.keys(states).length <= 1) {
            return { canDelete: false, reason: 'Cannot remove the last state' };
        }

        const isReferenced = Object.entries(states).some(([otherStateId, state]) => 
            otherStateId !== stateId && state.allowedNextStates?.includes(stateId)
        );

        if (isReferenced) {
            return { canDelete: false, reason: 'State is referenced by other states' };
        }

        return { canDelete: true };
    };

    /**
     * Selects a form type structure for editing and loads its configuration
     * @param {string} structureId - ID of the form type to select
     * @returns {Promise<boolean>} Promise that resolves with true if selection was successful, false otherwise
     * @throws {Error} If the structure cannot be loaded
     */
    const selectStructure = useCallback(async (structureId: string) => {
        try {
            const formType = await get<FormType>('formTypes', structureId);
            if (!formType) {
                return false;
            }
            
            setSelectedStructureId(structureId);
            setStructure(formType);
            setFormDerivesSaveRefused(false);

            dispatch({
                type: 'SELECT_STRUCTURE',
                payload: {
                    layout: formType.defaultUILayout,
                    description: formType.description,
                    possibleStates: formType.possibleStates,
                    allowedNextForms: formType.allowedNextForms,
                    splitConfiguration: formType.splitConfiguration,
                    protected: formType.protected,
                    formDerives: formType.formDerives
                }
            });
            return true;
        } catch (error) {
            console.error('Error selecting form type:', error);
            throw error;
        }
    }, [get, dispatch]);

    /**
     * Creates a new form type structure with default configuration
     * @returns {Promise<string>} Promise that resolves with the ID of the newly created form type
     */
    const createStructure = useCallback(async () => {
        try {
            const newFormType: Omit<FormType, 'docId'> = {
                defaultUILayout: {
                    structure: {
                        uiElementType: UIElementType.Column,
                        children: []
                    }
                },
                description: {
                    shortLabel: 'New Form Type',
                    shortDescription: '',
                    longLabel: '',
                    longDescription: ''
                },
                possibleStates: {
                    draft: {
                        allowedNextStates: ['draft']
                    }
                },
                allowedNextForms: []
            };

            const newFormTypeId = await add('formTypes', newFormType);
            setSelectedStructureId(newFormTypeId);
            dispatch({ 
                type: 'SELECT_LAYOUT', 
                payload: {
                    structure: {
                        uiElementType: UIElementType.Column,
                        children: [],
                        dndId: 'e0'
                    } as EditorUIContainer,
                }
            });
            return newFormTypeId;
        } catch (error) {
            console.error('Error creating form type:', error);
            throw error;
        }
    }, [add, dispatch]);

    /**
     * Archives a form type structure and clears selection. Rejects with the
     * backend error so the caller can surface its reason.
     * @param {string} structureId - ID of the form type to delete
     */
    const deleteStructure = useCallback(async (structureId: string) => {
        await archiveFormType(structureId);
        clearSelectedStructure();
        setSelectedStructureId(undefined);
        dispatch({ type: 'RESET_LAYOUT' });
    }, [archiveFormType, dispatch]);

    /**
     * Updates the description of the current form type
     * @param {keyof Description} field - The description field to update
     * @param {string} value - New value for the field
     * @returns {Promise<boolean>} Promise that resolves with true if update was successful, false otherwise
     */
    const updateDescription = useCallback(async (field: keyof Description, value: string) => {
        if (!structure || !selectedStructureId) return false;
        
        try {
            await set('formTypes', selectedStructureId, {
                description: {
                    ...structure.description,
                    [field]: value
                },
            });
            return true;
        } catch (error) {
            console.error('Error updating form type description:', error);
            throw error;
        }
    }, [structure, selectedStructureId, set]);

    /**
     * Updates the split configuration for the form type
     * @param config - The new split configuration or undefined to remove it
     */
    const handleSplitConfigurationChange = (config: FormSplitConfiguration | undefined) => {
        dispatch({
            type: 'UPDATE_SPLIT_CONFIGURATION',
            payload: config
        });
    };

    /**
     * Replaces the derived-value draft for the form type. Pushes a history entry,
     * so the toolbar's Save, Undo and Redo cover derive edits with no extra state.
     * @param formDerives - The complete new entry list
     */
    const handleFormDerivesChange = (formDerives: FormDeriveEntry[]) => {
        dispatch({
            type: 'UPDATE_FORM_DERIVES',
            payload: { formDerives }
        });
    };

    /**
     * Clears the current structure selection and resets layout state
     */
    const clearSelection = () => {
        setSelectedStructureId(undefined);
        dispatch({ type: 'RESET_LAYOUT' });
    };

    /**
     * Duplicates the current form type structure with a new name
     * @param {string} newName - Name for the duplicated form type
     * @returns {Promise<string>} Promise that resolves with the ID of the newly duplicated form type
     */
    const duplicateStructure = useCallback(async (newName: string) => {
        if (!structure || !selectedStructureId) {
            throw new Error('No structure selected to duplicate');
        }

        try {
            const duplicatedFormType: Omit<FormType, 'docId'> = {
                defaultUILayout: (
                    ({ meta, ...rest }) => rest)(structure.defaultUILayout as UILayout & { meta?: any }
                ),
                description: {
                    ...structure.description,
                    shortLabel: newName,
                    longLabel: `${newName} (Copy of ${structure.description?.longLabel || structure.description?.shortLabel || 'Untitled'})`
                },
                possibleStates: structure.possibleStates,
                allowedNextForms: structure.allowedNextForms,
                splitConfiguration: structure.splitConfiguration,
            };

            const newFormTypeId = await add('formTypes', duplicatedFormType);
            return newFormTypeId;
        } catch (error) {
            console.error('Error duplicating form type:', error);
            throw error;
        }
    }, [structure, selectedStructureId, add]);

    return {
        ...baseEditor,
        structure,
        structures,
        clearSelectedStructure,
        selectStructure,
        selectedStructureId,
        createStructure,
        deleteStructure,
        duplicateStructure,
        updateDescription,
        entities,
        handleStateChange,
        handleDescriptionChange,
        handleAllowedFormsChange,
        handleStatesUpdate,
        handleSplitConfigurationChange,
        handleFormDerivesChange,
        formDerivesSaveRefused,
        setFormDerivesSaveRefused,
        getStateRules,
        canRemoveState,
        clearSelection
    };
}; 